import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { FaUsers, FaCog, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import AdminLayout from '../components/AdminLayout';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedToday: 0,
    rejectedApplications: 0,
    avgResponseTime: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all employer applications
      const applicationsQuery = query(
        collection(db, 'employer_applications'),
        orderBy('createdAt', 'desc')
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applications = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate stats
      const totalApplications = applications.length;
      const pendingApplications = applications.filter(app => app.verificationStatus === 'pending').length;
      const rejectedApplications = applications.filter(app => app.verificationStatus === 'rejected').length;
      
      // Calculate applications approved today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const approvedToday = applications.filter(app => {
        if (app.verificationStatus === 'approved' && app.updatedAt) {
          const appDate = app.updatedAt.toDate ? app.updatedAt.toDate() : new Date(app.updatedAt);
          return appDate >= today;
        }
        return false;
      }).length;

      // Calculate average response time (in days)
      const processedApplications = applications.filter(app => 
        app.verificationStatus === 'approved' || app.verificationStatus === 'rejected'
      );
      
      let avgResponseTime = 0;
      if (processedApplications.length > 0) {
        const totalResponseTime = processedApplications.reduce((total, app) => {
          if (app.createdAt && app.updatedAt) {
            const createdDate = app.createdAt.toDate ? app.createdAt.toDate() : new Date(app.createdAt);
            const updatedDate = app.updatedAt.toDate ? app.updatedAt.toDate() : new Date(app.updatedAt);
            const diffTime = Math.abs(updatedDate - createdDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return total + diffDays;
          }
          return total;
        }, 0);
        avgResponseTime = (totalResponseTime / processedApplications.length).toFixed(1);
      }

      setStats({
        totalApplications,
        pendingApplications,
        approvedToday,
        rejectedApplications,
        avgResponseTime
      });

      // Set recent activity (last 10 applications with status changes)
      const recentActivities = applications
        .filter(app => app.verificationStatus !== 'pending')
        .slice(0, 10)
        .map(app => ({
          id: app.id,
          companyName: app.companyName,
          status: app.verificationStatus,
          updatedAt: app.updatedAt,
          action: getActionText(app.verificationStatus)
        }));

      setRecentActivity(recentActivities);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionText = (status) => {
    switch (status) {
      case 'approved': return 'Approved employer';
      case 'rejected': return 'Rejected application';
      case 'under_review': return 'Under review';
      default: return 'Status updated';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      case 'under_review': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - dateObj);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Less than an hour ago';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h2>
          <p className="text-gray-600">Welcome to your admin dashboard. Here's an overview of current activities.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-full">
                <FaUsers className="text-orange-500 text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Pending Reviews</h3>
                <p className="text-2xl font-bold text-orange-500">{stats.pendingApplications}</p>
                <p className="text-sm text-gray-600">Employer applications</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <FaCheckCircle className="text-green-500 text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Approved Today</h3>
                <p className="text-2xl font-bold text-green-500">{stats.approvedToday}</p>
                <p className="text-sm text-gray-600">New employers</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <FaClock className="text-blue-500 text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Avg Response Time</h3>
                <p className="text-2xl font-bold text-blue-500">{stats.avgResponseTime}</p>
                <p className="text-sm text-gray-600">Days</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-gray-500">
            <div className="flex items-center">
              <div className="p-3 bg-gray-100 rounded-full">
                <FaExclamationTriangle className="text-gray-500 text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Total Applications</h3>
                <p className="text-2xl font-bold text-gray-500">{stats.totalApplications}</p>
                <p className="text-sm text-gray-600">All time</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/verification"
                className="flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
              >
                <div className="flex items-center">
                  <FaUsers className="text-orange-500 mr-3" />
                  <span className="font-medium text-gray-900">Review Employer Applications</span>
                </div>
                {stats.pendingApplications > 0 && (
                  <div className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    {stats.pendingApplications} pending
                  </div>
                )}
              </Link>

              <Link
                to="/settings"
                className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="flex items-center">
                  <FaCog className="text-gray-500 mr-3" />
                  <span className="font-medium text-gray-900">Admin Settings</span>
                </div>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">System Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Database Connection</span>
                </div>
                <span className="text-sm font-medium text-green-600">Active</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Authentication Service</span>
                </div>
                <span className="text-sm font-medium text-green-600">Online</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Admin Portal</span>
                </div>
                <span className="text-sm font-medium text-green-600">Operational</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          {recentActivity.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No recent activity to display</p>
          ) : (
            <div className="space-y-3">
              {recentActivity.map((activity, index) => (
                <div key={activity.id} className="flex items-center py-3 border-b border-gray-100 last:border-b-0">
                  <div className={`w-2 h-2 ${getStatusColor(activity.status)} rounded-full mr-3 flex-shrink-0`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      {activity.action}: <span className="font-medium">{activity.companyName}</span>
                    </p>
                    <p className="text-xs text-gray-500">{formatTimeAgo(activity.updatedAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;